<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
// 나의 유형카드 / 인생의 에너지 흐름
import { VCol, VRow } from 'vuetify/lib/components/index.mjs';
import BasicContentsTitle from './BasicContentsTitle.vue';
import LifeCycleFlow from './LifeCycleFlow.vue';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';

const { sData } = storeToRefs(useAnalysisDataStore());

// 유형카드 데이터
const 유형카드 = computed(() => sData.value?.['유형카드']);

// 토글 상태 (하나만 열림)
const expandedCardIndex = ref(null);
const expandedCards = computed(() => {
  const result = {};
  if (expandedCardIndex.value !== null) {
    result[expandedCardIndex.value] = true;
  }
  return result;
});
const TOOLTIP_KEY = 'typeCardTooltipDismissed';
const showTooltip = ref(!sessionStorage.getItem(TOOLTIP_KEY));
const dismissTooltip = () => {
  showTooltip.value = false;
  sessionStorage.setItem(TOOLTIP_KEY, 'true');
};
const toggleCard = (index) => {
  expandedCardIndex.value = expandedCardIndex.value === index ? null : index;
  dismissTooltip();
};

// 퍼센트 문자열에서 숫자 추출 ("92.0%" -> 92.0)
const parsePercent = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  return parseFloat(value.replace('%', '')) || 0;
};

// 전체점수 객체를 배열로 변환
const parseScores = (scores) => {
  if (!scores) return [];
  return Object.entries(scores).map(([label, value]) => ({
    label,
    value: parsePercent(value),
  }));
};

// 유형 카드 목록
const 유형카드목록 = computed(() => {
  if (!유형카드.value) return [];
  return [
    {
      key: 'health',
      emoji: '💚',
      color: 'green',
      label: '건강 체질',
      name: 유형카드.value?.건강?.유형 || '-',
      description: 유형카드.value?.건강?.설명 || '',
      detailDescription: 유형카드.value?.건강?.상세설명 || '',
      features: 유형카드.value?.건강?.핵심특징 || [],
      advantages: 유형카드.value?.건강?.장점 || [],
      recommend: 유형카드.value?.건강?.추천 || '',
      style: 유형카드.value?.건강?.스타일 || '',
      cautions: 유형카드.value?.건강?.주의사항 || [],
      scores: parseScores(유형카드.value?.건강?.전체점수),
    },
    {
      key: 'relation',
      emoji: '❤️',
      color: 'red',
      label: '관계 유형',
      name: 유형카드.value?.관계?.유형 || '-',
      description: 유형카드.value?.관계?.설명 || '',
      detailDescription: 유형카드.value?.관계?.상세설명 || '',
      features: 유형카드.value?.관계?.핵심특징 || [],
      advantages: 유형카드.value?.관계?.장점 || [],
      recommend: 유형카드.value?.관계?.추천 || '',
      style: 유형카드.value?.관계?.스타일 || '',
      cautions: 유형카드.value?.관계?.주의사항 || [],
      scores: parseScores(유형카드.value?.관계?.전체점수),
    },
    {
      key: 'economy',
      emoji: '💙',
      color: 'blue',
      label: '경제 유형',
      name: 유형카드.value?.경제?.유형 || '-',
      description: 유형카드.value?.경제?.설명 || '',
      detailDescription: 유형카드.value?.경제?.상세설명 || '',
      features: 유형카드.value?.경제?.핵심특징 || [],
      advantages: 유형카드.value?.경제?.장점 || [],
      recommend: 유형카드.value?.경제?.추천 || '',
      style: 유형카드.value?.경제?.스타일 || '',
      cautions: 유형카드.value?.경제?.주의사항 || [],
      scores: parseScores(유형카드.value?.경제?.전체점수),
    },
    {
      key: 'growth',
      emoji: '💜',
      color: 'purple',
      label: '성장 유형',
      name: 유형카드.value?.성장?.유형 || '-',
      description: 유형카드.value?.성장?.설명 || '',
      detailDescription: 유형카드.value?.성장?.상세설명 || '',
      features: 유형카드.value?.성장?.핵심특징 || [],
      advantages: 유형카드.value?.성장?.장점 || [],
      recommend: 유형카드.value?.성장?.추천 || '',
      style: 유형카드.value?.성장?.스타일 || '',
      cautions: 유형카드.value?.성장?.주의사항 || [],
      scores: parseScores(유형카드.value?.성장?.전체점수),
    },
  ];
});

// 카드별 배경색 (연한 버전)
const getCardBgColor = (color) => {
  const colors = {
    green: 'rgba(136, 195, 74, 0.05)',
    red: 'rgba(255, 110, 110, 0.05)',
    blue: 'rgba(87, 144, 252, 0.05)',
    purple: 'rgba(117, 82, 255, 0.05)',
  };
  return colors[color] || 'rgba(117, 117, 117, 0.05)';
};

// 카드 액센트 색상
const getReliabilityColor = (color) => {
  const colors = {
    green: '#88c34a',
    red: '#ff6e6e',
    blue: '#5790fc',
    purple: '#7552ff',
  };
  return colors[color] || '#757575';
};

// 이모지 배경색
const getEmojiBgColor = (color) => {
  const colors = {
    green: 'rgba(136, 195, 74, 0.1)',
    red: 'rgba(255, 110, 110, 0.1)',
    blue: 'rgba(87, 144, 252, 0.1)',
    purple: 'rgba(117, 82, 255, 0.1)',
  };
  return colors[color] || 'rgba(117, 117, 117, 0.1)';
};
</script>

<template>
  <VRow class="ss-full-height">
    <!-- 왼쪽: 나의 유형카드 -->
    <VCol cols="12" :md="halfCol" class="ss-full-height">
      <div class="summ-card right">
        <BasicContentsTitle number="8" text="나의 유형카드" />
        <section class="summ-body">
          <div class="type-card-list">
            <div
              v-for="(card, index) in 유형카드목록"
              :key="index"
              class="type-card"
              :class="{ 'is-expanded': expandedCards[index] }"
              :style="expandedCards[index] ? { borderColor: getReliabilityColor(card.color) } : {}"
            >
              <div class="type-card__header">
                <div class="type-card__left">
                  <div
                    class="type-card__emoji"
                    :style="{ backgroundColor: getEmojiBgColor(card.color) }"
                  >
                    {{ card.emoji }}
                  </div>
                  <div class="type-card__info">
                    <span class="type-card__label">{{ card.label }}</span>
                    <span class="type-card__name">{{ card.name }}</span>
                  </div>
                </div>
                <div class="type-card__right">
                  <div class="type-card__toggle-wrapper">
                    <Transition name="tooltip-fade">
                      <div
                        v-if="index === 0 && showTooltip"
                        class="type-card__tooltip"
                        @click="dismissTooltip"
                      >
                        우측 아이콘을 눌러 상세 해석을 확인해보세요
                        <span class="type-card__tooltip-arrow" />
                      </div>
                    </Transition>
                    <button
                      class="type-card__toggle"
                      :class="{ 'is-rotated': expandedCards[index] }"
                      @click="toggleCard(index)"
                    />
                  </div>
                </div>
              </div>
              <div class="type-card__description">
                {{ card.description }}
              </div>

              <!-- 확장 영역 -->
              <Transition name="expand">
                <div v-if="expandedCards[index]" class="type-card__expand-wrapper">
                  <div class="type-card__divider" />

                  <!-- 상세설명 -->
                  <div class="type-card__detail-desc">
                    {{ card.detailDescription }}
                  </div>

                  <!-- 핵심특징 / 장점 -->
                  <div class="type-card__grid">
                    <div
                      class="type-card__grid-item"
                      :style="{ backgroundColor: getCardBgColor(card.color) }"
                    >
                      <p
                        class="type-card__grid-title"
                        :style="{ color: getReliabilityColor(card.color) }"
                      >
                        핵심 특징
                      </p>
                      <ul class="type-card__grid-list">
                        <li v-for="(item, idx) in card.features" :key="idx">{{ item }}</li>
                      </ul>
                    </div>
                    <div
                      class="type-card__grid-item"
                      :style="{ backgroundColor: getCardBgColor(card.color) }"
                    >
                      <p
                        class="type-card__grid-title"
                        :style="{ color: getReliabilityColor(card.color) }"
                      >
                        장점
                      </p>
                      <ul class="type-card__grid-list">
                        <li v-for="(item, idx) in card.advantages" :key="idx">{{ item }}</li>
                      </ul>
                    </div>
                  </div>

                  <!-- 추천 / 스타일 -->
                  <div class="type-card__grid">
                    <div
                      class="type-card__grid-item"
                      :style="{ backgroundColor: getCardBgColor(card.color) }"
                    >
                      <p
                        class="type-card__grid-title"
                        :style="{ color: getReliabilityColor(card.color) }"
                      >
                        추천
                      </p>
                      <p class="type-card__grid-text">{{ card.recommend }}</p>
                    </div>
                    <div
                      class="type-card__grid-item"
                      :style="{ backgroundColor: getCardBgColor(card.color) }"
                    >
                      <p
                        class="type-card__grid-title"
                        :style="{ color: getReliabilityColor(card.color) }"
                      >
                        스타일
                      </p>
                      <p class="type-card__grid-text">{{ card.style }}</p>
                    </div>
                  </div>

                  <!-- 주의사항 -->
                  <div class="type-card__caution">
                    <p class="type-card__caution-title">주의사항</p>
                    <ul class="type-card__caution-list">
                      <li v-for="(item, idx) in card.cautions" :key="idx">{{ item }}</li>
                    </ul>
                  </div>

                  <!-- 전체 유형 분포 -->
                  <div v-if="card.scores.length" class="type-card__scores">
                    <p class="type-card__scores-title">전체 유형 분포</p>
                    <div class="type-card__score-bars">
                      <div
                        v-for="(score, idx) in card.scores"
                        :key="idx"
                        class="type-card__score-bar"
                      >
                        <div class="type-card__score-bar-header">
                          <span class="type-card__score-bar-label">{{ score.label }}</span>
                          <span class="type-card__score-bar-value">{{ score.value }}%</span>
                        </div>
                        <div class="type-card__score-bar-track">
                          <div
                            class="type-card__score-bar-fill"
                            :style="{
                              width: `${score.value}%`,
                              backgroundColor: getReliabilityColor(card.color),
                            }"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </section>
      </div>
    </VCol>

    <!-- 오른쪽: 인생의 에너지 흐름 -->
    <VCol cols="12" :md="halfCol" class="ss-full-height">
      <div class="summ-card">
        <BasicContentsTitle number="9" text="인생의 에너지 흐름" />
        <section class="summ-body">
          <LifeCycleFlow :sData="sData" />
        </section>
      </div>
    </VCol>
  </VRow>
</template>

<style lang="scss" scoped>
// 유형카드 스타일
.type-card-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  // padding: 24px 18px;
}

.type-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  border: 1px solid #ebebeb;
  border-radius: 12px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__emoji {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    font-size: 20px;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__label {
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.3;
    color: #757575;
  }

  &__name {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    color: #333;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__toggle-wrapper {
    position: relative;
    height: 20px;
  }

  &__tooltip {
    position: absolute;
    bottom: calc(100% + 10px);
    right: -50%;
    transform: translateX(0%);
    white-space: nowrap;
    padding: 6px 12px;
    border-radius: 6px;
    background: #333;
    color: #fff;
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    z-index: 10;
    cursor: pointer;

    &-arrow {
      position: absolute;
      bottom: -4px;
      right: 5%;
      transform: translateX(-50%) rotate(45deg);
      width: 8px;
      height: 8px;
      background: #333;
    }
  }

  &__toggle {
    width: 20px;
    height: 20px;
    background: url('/src/assets/images_manse/chevron/chevron-down-circle.svg') no-repeat center;
    background-size: contain;
    border: none;
    cursor: pointer;
    transition: transform 0.2s;

    &.is-rotated {
      transform: rotate(180deg);
    }
  }

  &__description {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;
  }

  // 확장 영역 스타일
  &__expand-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__divider {
    height: 1px;
    background: #ebebeb;
    margin: 6px 0;
  }

  &__detail-desc {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;
    padding: 0 10px;
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  &__grid-item {
    padding: 12px 16px;
    border-radius: 4px;
  }

  &__grid-title {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    margin-bottom: 10px;
  }

  &__grid-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 400;
      line-height: 24px;
      color: #575757;

      &::before {
        content: '· ';
        font-weight: 700;
      }
    }
  }

  &__grid-text {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 22px;
    color: #575757;
    margin: 0;
  }

  &__caution {
    padding: 12px 16px;
    border-radius: 8px;
    background: rgba(242, 118, 71, 0.05);
  }

  &__caution-title {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    color: #f27647;
    margin-bottom: 8px;
  }

  &__caution-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4px;

    li {
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 400;
      line-height: 24px;
      color: #575757;

      &::before {
        content: '• ';
      }
    }
  }

  &__scores {
    margin-top: 10px;
  }

  &__scores-title {
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    color: #757575;
    text-align: right;
    margin-bottom: 10px;
  }

  &__score-bars {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__score-bar {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__score-bar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__score-bar-label {
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    color: #333;
  }

  &__score-bar-value {
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    color: #757575;
  }

  &__score-bar-track {
    height: 10px;
    background: #ebebeb;
    border-radius: 10px;
    overflow: hidden;
  }

  &__score-bar-fill {
    height: 100%;
    border-radius: 10px;
    animation: fillBar 0.8s ease-out forwards;
  }
}

@keyframes fillBar {
  from {
    width: 0%;
  }
}

// 말풍선 페이드 애니메이션
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: opacity 0.3s ease;
}

.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
}

// 확장 애니메이션
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
  opacity: 1;
  max-height: 1500px;
}
</style>
